import React from "react";
import { Breadcrumb as AntBreadcrumb } from "antd";
import { Link } from "react-router-dom";
import "./Breadcrumb.css";

/**
 * Standard app breadcrumb — presentational and prop-fed (no routing/store reads),
 * so it's trivial to unit-test and drop into Storybook. This is the reusable core;
 * `commons/breadcrumbContext` adds an optional stateful layer on top for pages
 * that prefer to declare their trail via a hook.
 *
 * @param {{
 *   crumbs?: Array<{ label: React.ReactNode, to?: string, onClick?: () => void }>,
 *   className?: string,
 * }} props
 *   - `to` → renders a react-router <Link> (preferred: a fixed, deep-link-safe target).
 *   - `onClick` → a clickable item (fallback when there's no route to link to).
 *   - The trailing crumb is the current page and always renders as plain text.
 */
export default function Breadcrumb({ crumbs = [], className }) {
  return (
    <AntBreadcrumb
      className={["app-breadcrumb", className].filter(Boolean).join(" ")}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        const linkable = !isLast && (crumb.to || crumb.onClick);
        // Key on the crumb's real identity (its route), not the array index.
        // The current-page crumb has no `to`, so fall back to its (string) label.
        const key =
          crumb.to ||
          (typeof crumb.label === "string" ? crumb.label : `crumb-${index}`);
        return (
          <AntBreadcrumb.Item
            key={key}
            onClick={linkable && !crumb.to ? crumb.onClick : undefined}
            className={linkable ? "app-breadcrumb-link" : undefined}
          >
            {linkable && crumb.to ? (
              <Link to={crumb.to}>{crumb.label}</Link>
            ) : (
              crumb.label
            )}
          </AntBreadcrumb.Item>
        );
      })}
    </AntBreadcrumb>
  );
}
